import { useQuery } from "@tanstack/react-query";
import { MapPin, CalendarDays, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import ImpactTimeline from "@/components/ImpactTimeline";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";
import { api } from "@/lib/api";
import { IMPACT_DIMENSIONS, IMPACT_CATEGORIES } from "@/lib/content";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

function EvidenceCard({ m }) {
  return (
    <div data-testid={`impact-card-${m.id}`} className="bg-white border border-ptf-primary/12 p-6 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-4xl md:text-5xl text-ptf-primary leading-none">
          {m.value}
          {m.unit && <span className="text-base ml-1.5 text-ptf-text/55 font-sans lowercase tracking-normal">{m.unit}</span>}
        </p>
        <EvidenceChip status={m.status || "DOCUMENTED"} />
      </div>
      <p className="mt-3 text-sm text-ptf-text/85 leading-snug flex-1">{m.label}</p>
      <dl className="mt-5 space-y-1.5 border-t border-ptf-primary/10 pt-4 text-[0.68rem] text-ptf-text/55">
        {m.location && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 shrink-0 text-ptf-secondary" />{m.location}</div>}
        {m.date && <div className="flex items-center gap-1.5"><CalendarDays className="h-3 w-3 shrink-0 text-ptf-secondary" />{m.date}</div>}
        {m.programme && <div className="uppercase tracking-[0.1em]">{m.programme}</div>}
        {m.source && <div className="flex items-center gap-1.5 italic"><FileText className="h-3 w-3 shrink-0 text-ptf-secondary" />{m.source}</div>}
        {m.last_verified && <div className="uppercase tracking-[0.08em] text-ptf-text/45">Last verified: {m.last_verified}</div>}
      </dl>
      {m.note && <p className="mt-3 text-[0.68rem] text-ptf-text/50 leading-relaxed">{m.note}</p>}
    </div>
  );
}

function DocumentedWork({ metrics }) {
  const grouped = IMPACT_CATEGORIES.map((c) => ({ cat: c, items: metrics.filter((m) => m.category === c) })).filter((g) => g.items.length > 0);
  const uncategorised = metrics.filter((m) => !IMPACT_CATEGORIES.includes(m.category));
  return (
    <section data-testid="documented-work" className="bg-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-4">
          <Reveal><Eyebrow>What has been documented</Eyebrow></Reveal>
          <MaskText
            lines={["Documented", "field work."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-text/70 leading-relaxed">
              Every entry carries its number, unit, location, date, programme, source
              and status. Old work stays historical; nothing is presented as current
              unless it is.
            </p>
          </Reveal>
        </div>
        {grouped.map((g) => (
          <div key={g.cat} className="mt-14">
            <Reveal>
              <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary border-b-2 border-ptf-primary/15 pb-3 mb-6">{g.cat}</h3>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {g.items.map((m, i) => (
                <Reveal key={m.id} delay={(i % 4) * 0.05} className="h-full"><EvidenceCard m={m} /></Reveal>
              ))}
            </div>
          </div>
        ))}
        {uncategorised.length > 0 && (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {uncategorised.map((m) => <EvidenceCard key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function BuildingNext() {
  return (
    <section data-testid="building-next" className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <Reveal><Eyebrow>What we are building</Eyebrow></Reveal>
          <h3 className="font-display uppercase text-3xl tracking-tight text-ptf-primary leading-none">The current chapter.</h3>
          <ul className="mt-6 space-y-3 text-ptf-text/75 leading-relaxed">
            <li>· The Last Horizon: Wildlife Filmmaking Challenge, Kaziranga &amp; Tanzania (Oct 2026)</li>
            <li>· The Dawn film, in development around a remote Uttarakhand community</li>
            <li>· Planet Trotter Learning & Training, formalising field skills into courses</li>
            <li>· Butt to Board, seeking support to take the initiative further</li>
          </ul>
        </div>
        <div>
          <Reveal delay={0.1}><Eyebrow>What comes next</Eyebrow></Reveal>
          <h3 className="font-display uppercase text-3xl tracking-tight text-ptf-primary leading-none">The next valley.</h3>
          <p className="mt-6 text-ptf-text/75 leading-relaxed">
            The Valley of River model (understand, connect, train, generate local
            revenue) is designed to travel. New locations will be announced only when
            a community has invited the work and the pilot is funded, and they will be
            documented here from day one.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function Impact() {
  const { data: metrics = [] } = useQuery({
    queryKey: ["impact"],
    queryFn: async () => (await api.get("/impact")).data,
  });

  return (
    <>
      <Seo title="Impact" path="/impact" description="Planet Trotter Foundation's evidence-based impact record: documented field interventions with dates, locations, programmes and sources." />
      <PageHero
        eyebrow="Impact"
        titleLines={["Documented,", "not claimed."]}
        intro="We do not talk about impact. We document the journey. Every figure below carries its date, location, programme and source, and old work is never presented as current."
        image="/dawn/vor-medicines.jpg"
      />

      <DocumentedWork metrics={metrics} />
      {metrics.length > 0 && <ImpactTimeline metrics={metrics} />}
      <BuildingNext />

      <section className="bg-ptf-primary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">How we report.</h2>
            <p className="mt-6 text-ptf-bg/75 leading-relaxed max-w-lg">
              For every programme, we document the context, the partners, the
              activities and the measured outcomes. Successes and shortfalls carry the
              same weight, and reports are published on the Transparency page as they
              become available.
            </p>
            <div className="mt-8"><PtfButton to="/transparency" variant="accent">View Transparency</PtfButton></div>
          </div>
          <div className="border-l border-ptf-bg/20 pl-8 space-y-7">
            {IMPACT_DIMENSIONS.map((d) => (
              <div key={d.key}>
                <p className="font-display uppercase text-2xl tracking-tight text-ptf-accent">{d.title}</p>
                <p className="mt-1 text-sm text-ptf-bg/65 leading-relaxed max-w-sm">{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
